import React, { useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import styles from "../../styles/components/product/ProductCard.module.css";
import { getSpecValue } from "../../utils/productSpecs";
import { useComparison } from "../../contexts/ComparisonContext";
import getProductImageUrl, { resolveProductImagePath } from "../../utils/imageUtils";
import {
  FaHeart,
  FaRegHeart,
  FaIndustry,
  FaSteamSquare,
  FaCalendar,
} from "react-icons/fa";
import capitalizeFirst from "../../utils/capitalizeFirst";
import api from "../../utils/api/axiosInstance";
import { useUserAuth } from "../../contexts/UserAuthContext";
import useProductMatchScore from "../../hooks/useProductMatchScore";
import { formatFitScoreLabel, formatFitScoreBadge } from "../../utils/matchScoreUtils";
import useCategoryDetails from "../../hooks/useCategoryDetails";
import useProductFavorite from "../../hooks/useProductFavorite";
import usePlatformEvents from "../../hooks/usePlatformEvents";
import { useRegion } from "../../contexts/RegionContext";
import { formatDisplayPrice } from "../../utils/formatPrice";
import * as FaIcons from "react-icons/fa";
import * as AiIcons from "react-icons/ai";
import * as BsIcons from "react-icons/bs";
import LazyImage from "../common/LazyImage"; // Phase 9: Lazy loading

const isMongoIdString = (value) =>
  typeof value === "string" && /^[a-f0-9]{24}$/i.test(value);

const ProductCard = ({
  product,
  view = "grid",
  categoryId,
  categoryName: categoryNameProp,
  onProductClick,
  disableHover = false,
  sessionId = null,
  rank = null,
  showMatchScore = true,
  precomputedMatchScore = null,
  openInNewTab = false,
}) => {
  const { user } = useUserAuth();
  const { isFavorite, toggleFavorite } = useProductFavorite(product?._id);
  const hookMatchScore = useProductMatchScore(product, {
    precomputed: precomputedMatchScore,
  });
  const matchScore = showMatchScore ? hookMatchScore : null;
  const { trackProduct } = usePlatformEvents();
  const { toggleProduct, isSelected } = useComparison();
  const isCompareSelected = isSelected(product._id);
  const { region } = useRegion();
  const displayPrice = formatDisplayPrice(product, region);

  const trackCardInteraction = (action, productId) => {
    const activeSessionId =
      sessionId || localStorage.getItem("currentSessionId");
    if (user) {
      trackProduct(action, productId, {
        sessionId: activeSessionId,
        surface: "product_card",
      });
    } else if (activeSessionId) {
      api
        .post("/analytics/interaction", {
          sessionId: activeSessionId,
          productId,
          action,
        })
        .catch(() => {});
    }
  };

  // Icon lookup
  const ICONS = { ...FaIcons, ...AiIcons, ...BsIcons };
  const IconByName = ({ name }) => {
    if (!name || typeof name !== "string") return null;
    const Icon = ICONS[name.trim()];
    return Icon ? <Icon className={styles.inlineIcon} /> : null;
  };

  // Resolve brand name consistently
  const brandName =
    product?.brand && typeof product.brand === "object"
      ? product.brand.name
      : product?.brand;

  // Resolve category for link if parent didn't pass categoryId
  const resolvedCategoryId =
    categoryId ||
    (typeof product?.category === "object"
      ? product.category._id
      : product?.category) ||
    "unknown";

  const { fields, allFields, name: fetchedCategoryName } =
    useCategoryDetails(
      resolvedCategoryId === "unknown" ? null : resolvedCategoryId,
    );
  const categoryFields = allFields || fields;

  const categoryName = useMemo(() => {
    if (categoryNameProp) return categoryNameProp;
    if (
      product?.category &&
      typeof product.category === "object" &&
      product.category.name
    ) {
      return product.category.name;
    }
    if (
      typeof product?.category === "string" &&
      product.category &&
      product.category !== "unknown" &&
      !isMongoIdString(product.category)
    ) {
      return product.category;
    }
    return fetchedCategoryName;
  }, [categoryNameProp, product?.category, fetchedCategoryName]);

  // Extract main fields from product (CPU, RAM, etc.)
  const mainFields = useMemo(() => {
    if (!product || !categoryFields.length) return [];

    // Get the first 4 main fields from category definition
    // Priority: CPU, RAM, Storage, GPU, etc.
    const priorityFields = [
      "CPU",
      "RAM",
      "Storage",
      "Year",
      "year",
      "GPU",
      "Screen",
      "Battery",
      "Memory",
      "Processor",
    ];

    let fields = categoryFields
      .map((field) => {
        // Try multiple ways to get the value (case-insensitive)
        const fieldName = field.name;
        const specValue = getSpecValue(product, fieldName);
        const value =
          specValue !== undefined && specValue !== null
            ? specValue
            : product[fieldName] ||
              product[fieldName?.toLowerCase()] ||
              product[fieldName?.toUpperCase()] ||
              product[
                fieldName?.charAt(0).toUpperCase() +
                  fieldName?.slice(1).toLowerCase()
              ];

        // Skip if value is empty, null, undefined, or empty string
        if (!value || value === "" || value === null || value === undefined) {
          return null;
        }

        return {
          name: fieldName,
          label: field.label || fieldName,
          value: String(value),
          icon: field.icon,
          priority:
            priorityFields.findIndex(
              (p) => p.toLowerCase() === fieldName.toLowerCase(),
            ) !== -1
              ? priorityFields.findIndex(
                  (p) => p.toLowerCase() === fieldName.toLowerCase(),
                )
              : 999,
        };
      })
      .filter(Boolean);

    const yearValue = product?.year;
    const hasYearField = fields.some(
      (field) => field.name?.toLowerCase() === "year",
    );
    if (yearValue && !hasYearField) {
      fields.push({
        name: "year",
        label: "Year",
        value: String(yearValue),
        icon: "FaCalendar",
        priority: priorityFields.findIndex(
          (p) => p.toLowerCase() === "year",
        ),
      });
    }

    return fields
      .sort((a, b) => a.priority - b.priority)
      .slice(0, 4); // Show 4 specs in both grid and list views
  }, [product, categoryFields]);

  const handleAddToFavorites = async () => {
    if (!product?._id) return;

    const result = await toggleFavorite();
    if (result.success && result.isFavorite) {
      trackCardInteraction("favorite", product._id);
    }
  };

  const handleCheckboxClick = (e) => {
    e.stopPropagation();
    // Don't prevent default - let the checkbox work normally
    // The onChange will handle the toggle
  };

  const handleCheckboxChange = (e) => {
    e.stopPropagation();
    // Don't prevent default - let the checkbox update visually
    // Toggle the product immediately
    const willSelect = !isCompareSelected;
    toggleProduct(product);
    if (willSelect) {
      trackCardInteraction("compare", product._id);
    }
  };

  const handleCheckboxContainerClick = (e) => {
    // Only prevent navigation if clicking the container, not the checkbox
    if (e.target.type !== "checkbox") {
      e.preventDefault();
      e.stopPropagation();
      // Toggle when clicking the container area
      const willSelect = !isCompareSelected;
      toggleProduct(product);
      if (willSelect) {
        trackCardInteraction("compare", product._id);
      }
    } else {
      // If clicking the checkbox itself, just stop propagation
      e.stopPropagation();
    }
  };

  const productPath = `/category/${resolvedCategoryId}/product/${product._id}`;

  const handleCardClick = useCallback(() => {
    if (onProductClick) {
      onProductClick(product._id);
    }
  }, [onProductClick, product._id]);

  return (
    <Link
      to={productPath}
      className={styles.productLink}
      onClick={handleCardClick}
      target={openInNewTab ? "_blank" : undefined}
    >
      {view === "compact" ? (
        <div className={`${styles.productCardCompact} cardShadow`}>
          {/* Rank Badge */}
          {rank && <div className={styles.rankBadge}>{rank}</div>}

          {/* Match Badge */}
          {matchScore !== null && (
            <div
              className={`${styles.matchBadge} ${
                matchScore >= 80
                  ? styles.matchBadgeExcellent
                  : matchScore >= 60
                    ? styles.matchBadgeGood
                    : styles.matchBadgeFair
              }`}
              title={formatFitScoreLabel(matchScore)}
            >
              {formatFitScoreBadge(matchScore)}
            </div>
          )}

          <div className={styles.compactImage}>
            <LazyImage
              src={getProductImageUrl(resolveProductImagePath(product))}
              alt={product.name}
              className={styles.productImg}
            />
          </div>

          <div className={styles.compactContent}>
            {categoryName && (
              <span className={styles.categoryLabel}>
                {capitalizeFirst(categoryName)}
              </span>
            )}
            <h4 className={styles.compactTitle}>
              {capitalizeFirst(product?.name) ||
                `${capitalizeFirst(brandName)} ${capitalizeFirst(
                  product.model,
                )}`}
            </h4>

            <div className={styles.compactPrice}>
              {displayPrice}
            </div>

            {/* Show description if available */}
            {product.description && (
              <div className={styles.compactDescription}>
                {product.description.length > 80
                  ? `${product.description.substring(0, 80)}...`
                  : product.description}
              </div>
            )}

            {/* Show key specs */}
            {mainFields.length > 0 && (
              <div className={styles.compactSpecs}>
                {mainFields.slice(0, 3).map((field, idx) => (
                  <div key={idx} className={styles.compactSpec}>
                    {field.icon ? (
                      <IconByName name={field.icon} />
                    ) : (
                      <FaSteamSquare />
                    )}
                    <span>
                      <b>{capitalizeFirst(field.label)}:</b>{" "}
                      {String(field.value)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div
            className={styles.favoriteIcon}
            title={isFavorite ? "Remove from favorites" : "Add to favorites"}
            onClick={(e) => {
              e.preventDefault(); // do not navigate when clicking the heart
              e.stopPropagation(); // do not let the click bubble to the card link
              handleAddToFavorites();
            }}
          >
            {isFavorite ? <FaHeart size={16} /> : <FaRegHeart size={16} />}
          </div>
        </div>
      ) : view === "list" ? (
        <div className={`${styles.productCardList} cardShadow`}>
          <div
            className={styles.compareCheckbox}
            onClick={handleCheckboxContainerClick}
            onMouseDown={(e) => {
              e.stopPropagation();
            }}
            title="Add to comparison"
          >
            <input
              type="checkbox"
              checked={isCompareSelected}
              onChange={handleCheckboxChange}
              onClick={handleCheckboxClick}
              aria-label={`${
                isCompareSelected ? "Remove" : "Add"
              } ${capitalizeFirst(product?.name) || "product"} ${
                isCompareSelected ? "from" : "to"
              } comparison`}
            />
          </div>

          {/* Match Badge */}
          {matchScore !== null && (
            <div
              className={`${styles.matchBadge} ${
                matchScore >= 80
                  ? styles.matchBadgeExcellent
                  : matchScore >= 60
                    ? styles.matchBadgeGood
                    : styles.matchBadgeFair
              }`}
              title={formatFitScoreLabel(matchScore)}
            >
              {formatFitScoreBadge(matchScore)}
            </div>
          )}

          <div className={styles.ListPicture}>
            <LazyImage
              src={getProductImageUrl(resolveProductImagePath(product))}
              alt={product.name}
              className={styles.productImg}
              style={{ width: "100%", height: "100%" }}
            />
          </div>

          <div className={styles.name}>
            {categoryName && (
              <span className={styles.categoryLabel}>
                {capitalizeFirst(categoryName)}
              </span>
            )}
            <h4 className={styles.productTitle}>
              {capitalizeFirst(product?.name) ||
                `${capitalizeFirst(brandName)} ${capitalizeFirst(
                  product.model,
                )}`}
            </h4>
          </div>

          <div className={styles.ListmetaBlock}>
            {/* Display main dynamic fields (CPU, RAM, etc.) */}
            {mainFields.length > 0 ? (
              mainFields.map((field, idx) => (
                <div key={idx} className={styles.ListmetaItem}>
                  {field.icon ? (
                    <IconByName name={field.icon} />
                  ) : (
                    <FaSteamSquare />
                  )}
                  <span>
                    <b>{capitalizeFirst(field.label)}:</b> {String(field.value)}
                  </span>
                </div>
              ))
            ) : (
              <>
                {/* Fallback to basic fields if no dynamic fields */}
                {brandName && (
                  <div className={styles.ListmetaItem}>
                    <FaIndustry />
                    <span>
                      <b>Brand:</b> {capitalizeFirst(brandName)}
                    </span>
                  </div>
                )}
                {product.model && (
                  <div className={styles.ListmetaItem}>
                    <FaSteamSquare />
                    <span>
                      <b>Model:</b> {product.model}
                    </span>
                  </div>
                )}
                {product.year && (
                  <div className={styles.ListmetaItem}>
                    <FaCalendar />
                    <span>
                      <b>Year:</b> {product.year}
                    </span>
                  </div>
                )}
              </>
            )}
          </div>

          <div className={styles.price}>{displayPrice}</div>

          <div
            className={styles.favoriteIcon}
            title={isFavorite ? "Remove from favorites" : "Add to favorites"}
            onClick={(e) => {
              e.preventDefault(); // do not navigate when clicking the heart
              e.stopPropagation(); // do not let the click bubble to the card link
              handleAddToFavorites();
            }}
          >
            {isFavorite ? <FaHeart size={20} /> : <FaRegHeart size={20} />}
          </div>
        </div>
      ) : (
        <div
          className={`${styles.productCardGrid} ${disableHover ? styles.noHover : ""} cardShadow`}
        >
          <div
            className={styles.compareCheckbox}
            onClick={handleCheckboxContainerClick}
            onMouseDown={(e) => {
              e.stopPropagation();
            }}
            title="Add to comparison"
          >
            <input
              type="checkbox"
              checked={isCompareSelected}
              onChange={handleCheckboxChange}
              onClick={handleCheckboxClick}
              aria-label={`${
                isCompareSelected ? "Remove" : "Add"
              } ${capitalizeFirst(product?.name) || "product"} ${
                isCompareSelected ? "from" : "to"
              } comparison`}
            />
          </div>

          {/* Match Badge */}
          {matchScore !== null && (
            <div
              className={`${styles.matchBadge} ${
                matchScore >= 80
                  ? styles.matchBadgeExcellent
                  : matchScore >= 60
                    ? styles.matchBadgeGood
                    : styles.matchBadgeFair
              }`}
              title={formatFitScoreLabel(matchScore)}
            >
              {formatFitScoreBadge(matchScore)}
            </div>
          )}

          <div
            className={styles.favoriteIcon}
            title={isFavorite ? "Remove from favorites" : "Add to favorites"}
            onClick={(e) => {
              e.preventDefault(); // do not navigate when clicking the heart
              e.stopPropagation(); // do not let the click bubble to the card link
              handleAddToFavorites();
            }}
          >
            {isFavorite ? <FaHeart size={16} /> : <FaRegHeart size={16} />}
          </div>

          {/* Image - with lazy loading (Phase 9) */}
          <div className={styles.productImgContainer}>
            <LazyImage
              src={getProductImageUrl(resolveProductImagePath(product))}
              alt={product.name}
              className={styles.productImg}
              style={{ width: "100%", height: "100%" }}
            />
          </div>

          {/* Title */}
          {categoryName && (
            <span className={styles.categoryLabel}>
              {capitalizeFirst(categoryName)}
            </span>
          )}
          <p className={styles.productTitle}>
            {product?.name
              ? capitalizeFirst(product.name)
              : `${capitalizeFirst(brandName)} ${capitalizeFirst(
                  product.model,
                )}`}
          </p>

          {/* Meta */}
          <div className={styles.metaBlock}>
            {/* Display main dynamic fields (CPU, RAM, etc.) */}
            {mainFields.length > 0 ? (
              mainFields.map((field, idx) => (
                <div key={idx} className={styles.metaItem}>
                  {field.icon ? (
                    <IconByName name={field.icon} />
                  ) : (
                    <FaSteamSquare />
                  )}
                  <span>
                    <b>{capitalizeFirst(field.label)}:</b> {String(field.value)}
                  </span>
                </div>
              ))
            ) : (
              <>
                {/* Fallback to basic fields if no dynamic fields */}
                {brandName && (
                  <div className={styles.metaItem}>
                    <FaIndustry />
                    <span>
                      <b>Brand:</b> {capitalizeFirst(brandName)}
                    </span>
                  </div>
                )}
                {product.model && (
                  <div className={styles.metaItem}>
                    <FaSteamSquare />
                    <span>
                      <b>Model:</b> {capitalizeFirst(product.model)}
                    </span>
                  </div>
                )}
                {product.year && (
                  <div className={styles.metaItem}>
                    <FaCalendar />
                    <span>
                      <b>Year:</b> {product.year}
                    </span>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Price */}
          <div className={styles.productPriceLine}>
            <span className={styles.productPrice}>
              {displayPrice}
            </span>
          </div>
        </div>
      )}
    </Link>
  );
};

export default ProductCard;
